// import { useMemo } from "react";
// import * as THREE from "three";

// // Helper to create edges geometry cache
// const edgesCache = new Map();

// function getEdgesGeometry(size) {
//   const key = size.join("-");
//   if (!edgesCache.has(key)) {
//     edgesCache.set(key, new THREE.EdgesGeometry(new THREE.BoxGeometry(...size)));
//   }
//   return edgesCache.get(key);
// }

// function BoxWithEdges({ obj }) {
//   const edges = useMemo(() => getEdgesGeometry(obj.size), [obj.size]);
//   const rotationY = obj.rotation ? (obj.rotation * Math.PI) / 180 : 0;

//   return (
//     <group position={obj.position} rotation={[0, obj.rotation, 0]}>
//       <mesh position={[0, 0, 0]}>
//         <boxGeometry args={obj.size} />
//         <meshBasicMaterial color={obj.color} />
//         <lineSegments geometry={edges}>
//           <lineBasicMaterial color="white" linewidth={10} />
//         </lineSegments>
//       </mesh>
//     </group>
//   );
// }

// export default BoxWithEdges;

import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

function DockBumperSet({ W, H, bumperTopHeight, bumperHeight, bumperThickness, bumperDepth, offsetZ }) {
  return (
    <group>
      {/* TOP bumper */}
      <mesh position={[0, H - bumperTopHeight / 1.5, offsetZ]} castShadow>
        <boxGeometry args={[W, bumperTopHeight, bumperDepth]} />
        <meshStandardMaterial color="#3b3a3a" roughness={0.85} metalness={0.1} />
      </mesh>

      {/* LEFT bumper */}
      <mesh position={[-W / 2 + bumperThickness / 2, H * 0.4, offsetZ]} castShadow>
        <boxGeometry args={[bumperThickness, bumperHeight, bumperDepth]} />
        <meshStandardMaterial color="#3b3a3a" roughness={0.85} metalness={0.1} />
      </mesh>

      {/* RIGHT bumper */}
      <mesh position={[W / 2 - bumperThickness / 2, H * 0.4, offsetZ]} castShadow>
        <boxGeometry args={[bumperThickness, bumperHeight, bumperDepth]} />
        <meshStandardMaterial color="#3b3a3a" roughness={0.85} metalness={0.1} />
      </mesh>
    </group>
  );
}

function ModernDock({ obj }) {
  const { size, position, rotation = 0 } = obj;

  const rotationY = (rotation * Math.PI) / 180;

  const W = size[0];
  const H = size[1];
  const D = size[2];

  // auto orientation (if width < depth -> rotate 90°)
  const isVertical = W < D;

  // When vertical, swap W and D for ALL calculations including geometry**
  const effectiveW = isVertical ? D : W;
  const effectiveD = isVertical ? W : D;

  // constants
  const frameThickness = Math.min(effectiveW, H) * 0.12;
  const shutterThickness = 0.05;
  const bumperWidth = effectiveW * 0.08;

  // bumper dimensions
  const bumperDepth = 0.18; // thicker looks more real
  const bumperThickness = 0.2; // along Z axis
  const bumperHeight = H * 0.75; // side bumpers taller now
  const bumperTopHeight = H * 0.2; // top bumper height
  const bumperPadding = effectiveW * 0.05;

  const matWidth = effectiveW - bumperWidth * 0.4;
  const matDepth = effectiveD * 1.6;

  // Inner opening (shutter size)
  const innerWidth = effectiveW - bumperThickness * 2 - bumperPadding * 2;
  const innerHeight = H - bumperTopHeight - H * 0.05;
  const shutterDepth = 0.08;

  // Rectangle shutter (fills the bumpers)
  const shutterGeometry = useMemo(() => {
    const shutter = new THREE.Mesh(
      new THREE.BoxGeometry(innerWidth, innerHeight, shutterDepth),
      new THREE.MeshStandardMaterial({
        color: "#777",
        metalness: 0.3,
        roughness: 0.6,
      })
    );

    return shutter;
  }, [innerWidth, innerHeight]);

  // Uncomment the following lines if you want to rotate the text with the camera
  // const { camera, scene, raycaster } = useThree();
  // const textRef = useRef();
  // useFrame(() => {
  //   if (textRef.current) {
  //     textRef.current.lookAt(camera.position);
  //   }
  // });

  return (
    <group rotation={[0, rotationY + (isVertical ? Math.PI / 2 : 0), 0]}>
      {/* Base wall behind dock - concrete texture */}
      <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[effectiveW, H, effectiveD]} />
        <meshStandardMaterial color="#8a8a8a" roughness={0.95} metalness={0.05} />
      </mesh>

      {/* Dock Door Frame - brushed metal */}
      <mesh position={[0, H * 0.55, effectiveD * 0.52]} castShadow>
        <boxGeometry args={[effectiveW, frameThickness, 0.1]} />
        <meshStandardMaterial color="#c0c4cc" metalness={0.6} roughness={0.3} envMapIntensity={1.2} />
      </mesh>

      {/* Vertical side frames - brushed metal */}
      <mesh position={[-effectiveW / 2 + frameThickness / 2, H * 0.2, effectiveD * 0.52]} castShadow>
        <boxGeometry args={[frameThickness, H * 0.7, 0.1]} />
        <meshStandardMaterial color="#c0c4cc" metalness={0.6} roughness={0.3} envMapIntensity={1.2} />
      </mesh>
      <mesh position={[effectiveW / 2 - frameThickness / 2, H * 0.2, effectiveD * 0.52]} castShadow>
        <boxGeometry args={[frameThickness, H * 0.7, 0.1]} />
        <meshStandardMaterial color="#c0c4cc" metalness={0.6} roughness={0.3} envMapIntensity={1.2} />
      </mesh>
      <mesh position={[-effectiveW / 2 + frameThickness / 2, H * 0.2, -effectiveD * 0.52]} castShadow>
        <boxGeometry args={[frameThickness, H * 0.7, 0.1]} />
        <meshStandardMaterial color="#c0c4cc" metalness={0.6} roughness={0.3} envMapIntensity={1.2} />
      </mesh>
      <mesh position={[effectiveW / 2 - frameThickness / 2, H * 0.2, -effectiveD * 0.52]} castShadow>
        <boxGeometry args={[frameThickness, H * 0.7, 0.1]} />
        <meshStandardMaterial color="#c0c4cc" metalness={0.6} roughness={0.3} envMapIntensity={1.2} />
      </mesh>

      {/* Shutter door - metallic with slight color variation */}
      <group>
        <primitive object={shutterGeometry} position={[0, innerHeight / 2, effectiveD * 0.56]} />
      </group>
      <group>
        <primitive object={shutterGeometry} position={[0, innerHeight / 2, -effectiveD * 0.56]} />
      </group>

      <group position={[0, 0, effectiveD * 0.58]}>
        <DockBumperSet
          W={effectiveW}
          H={H}
          bumperTopHeight={bumperTopHeight}
          bumperHeight={bumperHeight}
          bumperThickness={bumperThickness}
          bumperDepth={bumperDepth}
          offsetZ={effectiveD * 0.3}
        />
      </group>
      <group position={[0, 0, -effectiveD * 0.58]}>
        <DockBumperSet
          W={effectiveW}
          H={H}
          bumperTopHeight={bumperTopHeight}
          bumperHeight={bumperHeight}
          bumperThickness={bumperThickness}
          bumperDepth={bumperDepth}
          offsetZ={-effectiveD * 0.3}
        />
      </group>

      {/* Floor Mat - dark textured rubber */}
      <mesh position={[0, 0.01, effectiveD * 0.8]} receiveShadow>
        <boxGeometry args={[matWidth, 0.02, matDepth]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.98} metalness={0} />
      </mesh>
      <mesh position={[0, 0.01, -effectiveD * 0.8]} receiveShadow>
        <boxGeometry args={[matWidth, 0.02, matDepth]} />
        <meshStandardMaterial color="#2f2e2e" roughness={0.98} metalness={0} />
      </mesh>
      {/* FLOATING DOCK LABEL ABOVE THE DOCK */}
      {/* <Text
        ref={textRef}
        position={[0, H + 1.2, D * 0.3]}
        rotation={[0, 0, 0]} // no counter-rotation
        fontSize={H * 0.15}
        color="#222"
        fontWeight={"bold"}
        anchorX="center"
        anchorY="middle"
      >
        {obj?.name || "DOCK"}
      </Text> */}
    </group>
  );
}

export default ModernDock;
